import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { CalendarDays, Users, Receipt, TrendingUp, ArrowUpRight, Bell, AlertTriangle } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import WhatsAppNotificationsWidget from "@/components/WhatsAppNotificationsWidget";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sq-AL", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="surface-card p-6" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EAE4D9] text-[#2C4C3B]">
          <Icon size={18} />
        </div>
        <ArrowUpRight size={16} className="text-[#5C5C5C]" />
      </div>
      <div className="mt-6">
        <div className="label-eyebrow">{label}</div>
        <div className="mt-1 text-3xl font-semibold tracking-tight text-[#1A1A1A]">{value}</div>
        {hint && <div className="mt-1 text-xs text-[#5C5C5C]">{hint}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { format } = useCurrency();
  const [stats, setStats] = useState(null);
  const [recallStats, setRecallStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/recalls/stats").then(({ data }) => setRecallStats(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Paneli Kryesor</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Mirëmëngjes 👋</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Pamje e shpejtë e aktivitetit të klinikës sot.</p>
        </div>
        <Link to="/pacientet" className="btn-primary" data-testid="quick-add-patient">
          Shto Pacient
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Users} label="Totali i Pacientëve" value={stats?.total_patients ?? "—"} testId="stat-patients" />
        <StatCard icon={CalendarDays} label="Takimet Sot" value={stats?.appointments_today ?? "—"} testId="stat-appts-today" />
        <StatCard icon={TrendingUp} label="Të Ardhurat (muaj)" value={stats ? format(stats.month_revenue) : "—"} testId="stat-revenue" />
        <StatCard icon={Receipt} label="Fatura të Papaguara" value={stats?.unpaid_invoices ?? "—"} testId="stat-unpaid" />
      </div>

      {/* WhatsApp notifications (only shown when items exist) */}
      <WhatsAppNotificationsWidget />

      {/* Recall alerts widget */}
      {recallStats && (recallStats.overdue > 0 || recallStats.due_soon > 0) && (
        <Link to="/kujtuesit" className="block surface-card p-6 border-l-4 border-l-[#9E3C3C] hover:shadow-md transition-shadow" data-testid="recall-alert-widget">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#9E3C3C]/15 text-[#9E3C3C]">
                {recallStats.overdue > 0 ? <AlertTriangle size={22} /> : <Bell size={22} />}
              </div>
              <div>
                <div className="label-eyebrow">Kujtuesit (Recalls)</div>
                <div className="mt-1 text-lg font-semibold">
                  {recallStats.overdue > 0 && <span className="text-[#9E3C3C]">{recallStats.overdue} të skaduar</span>}
                  {recallStats.overdue > 0 && recallStats.due_soon > 0 && " · "}
                  {recallStats.due_soon > 0 && <span className="text-[#8B6914]">{recallStats.due_soon} brenda javës</span>}
                </div>
                <div className="text-xs text-[#5C5C5C]">Pacientë që duhet të kontaktohen për vizitë periodike</div>
              </div>
            </div>
            <div className="text-sm font-medium text-[#2C4C3B]">Shiko të gjithë →</div>
          </div>
        </Link>
      )}

      <div className="surface-card p-8" data-testid="upcoming-appointments">
        <div className="flex items-end justify-between">
          <div>
            <div className="label-eyebrow">Takimet në Vijim</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Pesë takimet e ardhshme</h2>
          </div>
          <Link to="/takimet" className="text-sm font-medium text-[#2C4C3B] hover:text-[#3D6A52]">
            Shiko të gjitha →
          </Link>
        </div>

        <div className="mt-6 divide-y divide-[#E2DFD8]">
          {(stats?.upcoming_appointments || []).length === 0 && (
            <div className="py-10 text-center text-sm text-[#5C5C5C]">
              Nuk ka takime të planifikuara.
            </div>
          )}
          {(stats?.upcoming_appointments || []).map((a) => (
            <div key={a.id} className="flex items-center justify-between py-4" data-testid={`upcoming-${a.id}`}>
              <div>
                <div className="font-medium text-[#1A1A1A]">{a.patient_name}</div>
                <div className="text-sm text-[#5C5C5C]">{a.reason || "Konsultë"}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-[#2C4C3B]">{fmtDateTime(a.start_time)}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">{a.status.replace("_", " ")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
